import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  emailWOValueValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import {
  createWebBranch,
  getWebBranchByID,
  updateWebBranch,
} from "./services/webBranch.services";
import { clearWebBranchDetails } from "./webBranchSlice";
import { getAllActiveCities } from "../city/services/city.services";
import { ICity } from "../city/cityModel";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormRichTextField from "../../components/formFields/FormRichTextField";

const CreateWebBranch: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const WebBranchID = location.state?.WebBranchID;

  const WebBranchDetail = useAppSelector(
    (state) => state.webBranch.WebBranchDetail
  );
  const loading = useAppSelector((state) => state.webBranch.loading);
  const CityList = useAppSelector((state) => state.city.CityList);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [WebBranchDetail]);

  useEffect(() => {
    if (WebBranchID) {
      dispatch(getWebBranchByID(WebBranchID));
    }
    return () => {
      dispatch(clearWebBranchDetails());
    };
  }, [WebBranchID]);

  useEffect(() => {
    dispatch(getAllActiveCities());
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (WebBranchID) {
        const editPayload = {
          ID: WebBranchID,
          BranchName: values.BranchName ? values.BranchName : "",
          Address: values.Address ? values.Address : "",
          CityID: values.CityID ? +values.CityID : null,
          MobileNo: values.MobileNo ? `${values.MobileNo}` : "",
          EmailID: values.EmailID ? values.EmailID : "",
          MapURL: values.MapURL ? values.MapURL : "",
          WorkingHours: values.WorkingHours ? values.WorkingHours : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateWebBranch(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          BranchName: values.BranchName ? values.BranchName : "",
          Address: values.Address ? values.Address : "",
          CityID: values.CityID ? +values.CityID : null,
          MobileNo: values.MobileNo ? `${values.MobileNo}` : "",
          EmailID: values.EmailID ? values.EmailID : "",
          MapURL: values.MapURL ? values.MapURL : "",
          WorkingHours: values.WorkingHours ? values.WorkingHours : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createWebBranch(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/webbranch");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={WebBranchDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {WebBranchID ? "Update Web Branch" : "Add Web Branch"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="BranchName"
                      label="Branch Name"
                      placeholder="i.e. DriveIn"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Address"
                      label="Address"
                      placeholder="Address"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"CityID"}
                      name={"CityID"}
                      label={"City"}
                      placeholder="i.e. Ahmedabad"
                      component={FormSelectionField}
                      // validator={requiredValidator}
                      // astrike={true}
                      options={CityList?.map((city: ICity) => {
                        return {
                          value: city?.ID,
                          label: city?.CityName,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="MobileNo"
                      label="Mobile No"
                      placeholder="i.e. 9999999999"
                      component={FormTextField}
                      astrike={true}
                      validator={requiredValidator}
                      onChange={(e: any) => {
                        const filteredMobileNo = e?.value?.replace(
                          /[^0-9+, ]/g,
                          ""
                        );
                        formRenderProps.onChange("MobileNo", {
                          value: filteredMobileNo,
                        });
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="EmailID"
                      label="Email ID"
                      placeholder="i.e. example@example.com"
                      component={FormTextField}
                      validator={emailWOValueValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="WorkingHours"
                      label="Working Hours"
                      placeholder="Working Hours"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem rowSpan={4} colSpan={3}>
                    <Field
                      name="MapURL"
                      label="Map URL"
                      placeholder="Map URL"
                      component={FormRichTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={WebBranchID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/webbranch")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateWebBranch;
